import { useState } from 'react';

function normalizeImages(images) {
  const urls = [];
  const seen = new Set();

  for (const item of images || []) {
    const url =
      typeof item === 'string'
        ? item.trim()
        : String(item?.src || item?.url || item?.imageUrl || '').trim();
    if (!url) continue;
    const key = url.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    urls.push(url);
  }

  return urls;
}

export function getProductImages(product) {
  const fromList = normalizeImages(
    product?.image_urls || product?.imageUrls || product?.images
  );
  const single = String(product?.image_url || product?.imageUrl || '').trim();
  if (single) {
    const key = single.toLowerCase();
    if (!fromList.some((url) => url.toLowerCase() === key)) {
      fromList.unshift(single);
    }
  }
  return fromList;
}

export default function ProductImageCarousel({ images, alt }) {
  const slides = normalizeImages(images);
  const [index, setIndex] = useState(0);
  const [failed, setFailed] = useState(() => new Set());

  const visibleSlides = slides.filter((_, slideIndex) => !failed.has(slideIndex));

  if (!slides.length || !visibleSlides.length) {
    return <div className="live-card-image-fallback">No image</div>;
  }

  const safeIndex = Math.min(index, visibleSlides.length - 1);
  const currentSlide = visibleSlides[safeIndex];
  const hasMultiple = visibleSlides.length > 1;

  const go = (direction) => {
    setIndex((current) => (current + direction + visibleSlides.length) % visibleSlides.length);
  };

  const handleImageError = () => {
    const originalIndex = slides.indexOf(currentSlide);
    setFailed((prev) => {
      const next = new Set(prev);
      if (originalIndex >= 0) next.add(originalIndex);
      return next;
    });
    setIndex((current) => Math.min(current, Math.max(visibleSlides.length - 2, 0)));
  };

  return (
    <div className="live-card-carousel">
      <img
        src={currentSlide}
        alt={alt}
        className="live-card-image"
        loading="lazy"
        onError={handleImageError}
      />

      {hasMultiple ? (
        <>
          <button
            type="button"
            className="live-card-carousel-btn live-card-carousel-prev"
            onClick={() => go(-1)}
            aria-label="Previous image"
          >
            ‹
          </button>
          <button
            type="button"
            className="live-card-carousel-btn live-card-carousel-next"
            onClick={() => go(1)}
            aria-label="Next image"
          >
            ›
          </button>
          <div className="live-card-carousel-counter">
            {safeIndex + 1}/{visibleSlides.length}
          </div>
          <div className="live-card-carousel-dots">
            {visibleSlides.map((url, dotIndex) => (
              <button
                key={url}
                type="button"
                className={`live-card-carousel-dot${dotIndex === safeIndex ? ' active' : ''}`}
                onClick={() => setIndex(dotIndex)}
                aria-label={`Show image ${dotIndex + 1}`}
              />
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}
